import { useIntl } from "react-intl";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Typography, Container, Paper, Divider, IconButton } from "@mui/material";

//Components
import InfoIcon from '@mui/icons-material/Info';
import { NotificationManager } from 'Components/Notifications';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import VidfleetInformation from '../../../.common/.components/vidfleetInformation';
import { NavLink } from "react-router-dom";
import { Tooltip } from "@gpstrackit/react-controls";

//Helpers
import { getDateFormatted } from 'Core/data/Helpers';

//Actions
import {
    whiteListedDevicesRequest,
    whiteListedDevicesReset,
    vidfleetInformationOpenModal
} from 'Redux/actions';

const WhiteListedDevices = (props) => {
    const { esn } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const [columns, setColumns] = useState([
        {
            key: "camerasn",
            title: messages['vidfleet_camera_sm'],
            show: true,
            sort: false,
            locked: true,
            resize: true,
            export: true,
            exportCell: "esn",
            code: "vidfleet_camera_sm",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item?.esn}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.esn}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "account",
            title: messages['vidfleet_account'],
            show: true,
            sort: false,
            locked: false,
            resize: true,
            export: true,
            exportCell: "device.company.name",
            code: "vidfleet_account",
            render: (item) => {
                return <span>{item?.device?.company?.name || ""}</span>
            }
        },
        {
            key: "devicename",
            title: messages['vidfleet_device_name'],
            show: true,
            sort: false,
            locked: false,
            resize: true,
            export: true,
            exportCell: "device.label",
            code: "vidfleet_device_name",
            render: (item) => {
                return <span>{item?.device?.label || ""}</span>
            }
        },
        {
            key: "devicemodel",
            title: messages['vidfleet_device_model'],
            show: true,
            sort: false,
            locked: false,
            resize: true,
            export: true,
            exportCell: "device.deviceModel.name",
            code: "vidfleet_device_model",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 5 }}>{item?.device?.deviceModel?.name}</span>
                        {
                            item?.device?.deviceModel &&
                            <Tooltip
                                title={messages['vidfleet_show_vidfleet_info']}
                            >
                                <IconButton
                                    style={{ padding: 0, color: '#444444' }}
                                    onClick={() => {
                                        dispatch(vidfleetInformationOpenModal(item.esn, item?.device?.id, item?.device?.timezone, item?.device?.companyId))
                                    }}
                                    size="large">
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </div>
                );
            }
        },
        {
            key: "createat",
            title: messages['vidfleet_created_at'],
            show: true,
            sort: false,
            locked: false,
            resize: true,
            export: true,
            exportCell: "device.createdOn",
            exportFormat: "datetime",
            code: "vidfleet_created_at",
            render: (item) => {
                return <span>{item?.device?.createdOn && getDateFormatted(item?.device?.createdOn, "date", "datetime")}</span>
            }
        },
    ])

    const { loading, Items, error, errorCode, total } = useSelector(state => state.whiteListedDevicesRedux);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    useEffect(() => {
        if (Items && Items.length === 0) {
            dispatch(whiteListedDevicesRequest({ page: 0, rowsPerPage }));
        }
    }, [dispatch, rowsPerPage]);

    useEffect(() => {
        if (error) {
            if (esn) {
                NotificationManager.error(
                    "",
                    errorCode === 403 ? messages['vidfleet_the_esn_could_not_be_consulted'] : messages['vidfleet_the_esn_does_not_exist'],
                    3000,
                    null,
                    null,
                    'error'
                );
            } else {
                NotificationManager.error(
                    "",
                    messages['vidfleet_white_listed_devices_error'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            dispatch(whiteListedDevicesReset({ error: null, errorCode: null }));
        }
    }, [error, dispatch, esn, messages])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['vidfleet_white_listed_devices']}
                        </Typography>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        name="pm-vidfleet-white-listed"
                        infiniteScroll={false}
                        columns={columns}
                        onChangeColumns={(items) => setColumns(items)}
                        externalDataConfig={{
                            data: Items,
                            loading,
                            total,
                            totalCount: total,
                            totalCountLoading: loading,
                            onRefresh: () => dispatch(whiteListedDevicesRequest({ page, rowsPerPage, refresh: true })),
                        }}
                        onChangePage={(_nextToken, newPage) => {
                            const zeroIndexedPage = (newPage || 1) - 1;
                            setPage(zeroIndexedPage);
                            dispatch(whiteListedDevicesRequest({ page: zeroIndexedPage, rowsPerPage }));
                        }}
                        onChangeRowsPerPage={(rows) => {
                            setRowsPerPage(rows);
                            setPage(0);
                            dispatch(whiteListedDevicesRequest({ page: 0, rowsPerPage: rows }));
                        }}
                        tableButtons={["export", "orderColumn"]}
                        exportConfig={{ fileName: "whitelisteddevices", useDataGrid: true }}
                        searchConfig={{ disabled: true }}
                        selectionConfig={{ disabled: true }}
                        uiConfig={{ hideFilters: true, hideSplit: true, topHeight: 200, hideAllInPagination: true }}
                        defaultsConfig={{ rowsPerPage: 50, valuesPerPage: [50, 100, 200] }}
                    />
                </Grid>
            </Paper>
            <VidfleetInformation />
        </Container>
    )
}
export default WhiteListedDevices;